import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AccountStatus, CustomerAccountDetail, PortalSignupMode } from '@central/contracts';
import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { mensagemDeErro } from '@/lib/format';
import { Campo, CLASSE_DE_CAMPO } from '@/features/admin/componentes';
import { alterarCliente, chavesDeClientes } from '@/features/clientes/clientes.api';
import { MODOS_DE_AUTOCADASTRO, SITUACOES_DE_CONTA, situacaoDeConta } from '@/features/clientes/navegacao';
import { Modal } from '@/features/tickets/Modal';

/**
 * As peças que as duas telas de cliente compartilham.
 *
 * A tabela, o campo de formulário e a janela modal **não** estão aqui: eles
 * já existem em `features/admin/componentes` e em `features/tickets/Modal`, e
 * são reusados pelo alias. É a mesma decisão que aquele arquivo tomou ao
 * reusar o `Modal` em vez de copiá-lo — duas tabelas divergem no primeiro
 * ajuste de espaçamento, e duas modais divergem no primeiro ajuste de foco.
 *
 * O que está aqui é o que só cliente tem: a etiqueta de situação, que é de
 * quatro estados e não de dois, e o envoltório de escrita, que invalida as
 * chaves desta feature.
 */

const TONS = {
  neutro: 'bg-muted text-muted-foreground ring-border',
  sucesso: 'bg-success/12 text-success ring-success/25',
  alerta: 'bg-warning/12 text-warning ring-warning/25',
  perigo: 'bg-destructive/12 text-destructive ring-destructive/25',
} as const;

export type TomDeSituacao = keyof typeof TONS;

/**
 * Situação em cor **e** em palavra.
 *
 * Cor sozinha não comunica para daltônicos, e aqui são quatro estados, não
 * dois: a distância entre "suspenso" e "encerrado" precisa estar escrita.
 */
export function EtiquetaDeSituacao({
  rotulo,
  tom,
  titulo,
}: {
  rotulo: string;
  tom: TomDeSituacao;
  titulo?: string;
}) {
  return (
    <span
      title={titulo}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset',
        TONS[tom],
      )}
    >
      {rotulo}
    </span>
  );
}

/** "3 chamados", "nenhum" — o número que a confirmação vai repetir. */
export function Contagem({
  valor,
  singular,
  plural,
}: {
  valor: number;
  singular: string;
  plural: string;
}) {
  if (valor === 0) return <span className="text-xs text-muted-foreground">nenhum</span>;
  return (
    <span className="text-xs tabular-nums">
      {valor} {valor === 1 ? singular : plural}
    </span>
  );
}

/**
 * Envolve uma escrita de cliente: invalida a raiz da feature e guarda o erro.
 *
 * Invalidar a raiz inteira é deliberado — cadastrar um contato muda a contagem
 * de contatos da conta na lista de clientes, e desativar um cliente muda o que
 * o seletor de organização oferece. Uma invalidação cirúrgica aqui seria uma
 * lista de chaves para esquecer de atualizar.
 */
export function useEscritaDeCliente<TEntrada = void, TSaida = unknown>(
  escrever: (entrada: TEntrada) => Promise<TSaida>,
  aoConcluir?: (saida: TSaida) => void,
) {
  const cliente = useQueryClient();
  const [erro, setErro] = useState<string | null>(null);

  const mutacao = useMutation<TSaida, Error, TEntrada>({
    mutationFn: escrever,
    onMutate: () => setErro(null),
    onSuccess: (saida) => {
      void cliente.invalidateQueries({ queryKey: chavesDeClientes.raiz });
      // As telas de chamado leem organização por `/lookups`: sem isto, o
      // cliente recém-criado só apareceria no formulário meia hora depois.
      void cliente.invalidateQueries({ queryKey: ['apoio'] });
      aoConcluir?.(saida);
    },
    onError: (falha) => setErro(mensagemDeErro(falha) ?? 'Não foi possível salvar.'),
  });

  return { mutacao, erro, limparErro: () => setErro(null) };
}

/**
 * Modal de edição de cliente — compartilhado pela lista e pela ficha.
 *
 * Precisa do `CustomerAccountDetail` completo porque usa `version` para
 * concorrência otimista e `domain` que não vem no resumo da lista.
 */
export function DialogoDeEdicaoDeCliente({
  aberto,
  cliente,
  aoFechar,
}: {
  aberto: boolean;
  cliente: CustomerAccountDetail;
  aoFechar: () => void;
}) {
  const [codigo, setCodigo] = useState(cliente.code);
  const [razaoSocial, setRazaoSocial] = useState(cliente.legalName);
  const [nomeFantasia, setNomeFantasia] = useState(cliente.tradeName ?? '');
  const [cnpj, setCnpj] = useState(cliente.taxId ?? '');
  const [dominio, setDominio] = useState(cliente.domain ?? '');
  const [status, setStatus] = useState<AccountStatus>(cliente.status);
  const [portalEnabled, setPortalEnabled] = useState(cliente.portalEnabled);
  const [autocadastro, setAutocadastro] = useState<PortalSignupMode>(cliente.portalSelfSignup);
  const [dominiosDoPortal, setDominiosDoPortal] = useState(cliente.portalEmailDomains.join(', '));
  const [observacoes, setObservacoes] = useState(cliente.notes ?? '');
  const [semeado, setSemeado] = useState<string | null>(null);

  const identidade = `${cliente.id}:${cliente.version}`;
  if (aberto && semeado !== identidade) {
    setSemeado(identidade);
    setCodigo(cliente.code);
    setRazaoSocial(cliente.legalName);
    setNomeFantasia(cliente.tradeName ?? '');
    setCnpj(cliente.taxId ?? '');
    setDominio(cliente.domain ?? '');
    setStatus(cliente.status);
    setPortalEnabled(cliente.portalEnabled);
    setAutocadastro(cliente.portalSelfSignup);
    setDominiosDoPortal(cliente.portalEmailDomains.join(', '));
    setObservacoes(cliente.notes ?? '');
  }
  if (!aberto && semeado !== null) setSemeado(null);

  const escrita = useEscritaDeCliente(
    () =>
      alterarCliente(cliente.id, {
        code: codigo.trim(),
        legalName: razaoSocial.trim(),
        tradeName: nomeFantasia.trim(),
        taxId: cnpj.trim(),
        domain: dominio.trim().toLowerCase(),
        status,
        portalEnabled,
        portalSelfSignup: autocadastro,
        portalEmailDomains: dominiosDoPortal
          .split(',')
          .map((d) => d.trim().toLowerCase())
          .filter(Boolean),
        notes: observacoes.trim(),
        version: cliente.version,
      }),
    aoFechar,
  );

  const efeito = situacaoDeConta(status).efeito;
  const mudouParaInativo =
    status !== cliente.status && (status === 'SUSPENDED' || status === 'CHURNED');

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    escrita.mutacao.mutate();
  }

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={`Editar "${cliente.name}"`}
      descricao="A situação comercial governa o portal: suspender ou encerrar derruba as sessões abertas dos contatos deste cliente."
    >
      <form onSubmit={enviar} className="space-y-4 px-5 py-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="ed-codigo" rotulo="Código">
            <input
              id="ed-codigo"
              value={codigo}
              onChange={(evento) => setCodigo(evento.target.value)}
              required
              maxLength={32}
              className={`${CLASSE_DE_CAMPO} font-mono`}
            />
          </Campo>

          <Campo id="ed-status" rotulo="Situação">
            <select
              id="ed-status"
              value={status}
              onChange={(evento) => setStatus(evento.target.value as AccountStatus)}
              className={CLASSE_DE_CAMPO}
            >
              {SITUACOES_DE_CONTA.map((situacao) => (
                <option key={situacao.valor} value={situacao.valor}>
                  {situacao.rotulo}
                </option>
              ))}
            </select>
          </Campo>
        </div>

        <p
          className={
            mudouParaInativo
              ? 'rounded-md bg-warning/10 px-3 py-2 text-xs text-warning'
              : 'text-xs text-muted-foreground'
          }
        >
          {efeito}
        </p>

        <Campo id="ed-razao" rotulo="Razão social">
          <input
            id="ed-razao"
            value={razaoSocial}
            onChange={(evento) => setRazaoSocial(evento.target.value)}
            required
            minLength={2}
            maxLength={200}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo id="ed-fantasia" rotulo="Nome fantasia" opcional>
            <input
              id="ed-fantasia"
              value={nomeFantasia}
              onChange={(evento) => setNomeFantasia(evento.target.value)}
              maxLength={200}
              className={CLASSE_DE_CAMPO}
            />
          </Campo>

          <Campo id="ed-cnpj" rotulo="CNPJ" opcional>
            <input
              id="ed-cnpj"
              value={cnpj}
              onChange={(evento) => setCnpj(evento.target.value)}
              maxLength={20}
              className={`${CLASSE_DE_CAMPO} font-mono text-xs`}
            />
          </Campo>
        </div>

        <Campo id="ed-dominio" rotulo="Domínio de e-mail" opcional dica="Só o domínio: contoso.com.br.">
          <input
            id="ed-dominio"
            value={dominio}
            onChange={(evento) => setDominio(evento.target.value)}
            maxLength={253}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <fieldset className="space-y-4 rounded-md border border-border p-4">
          <legend className="px-1 text-sm font-medium">Portal do cliente</legend>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={portalEnabled}
              onChange={(evento) => setPortalEnabled(evento.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input"
            />
            <span>
              Habilitar o portal para este cliente
              <span className="mt-0.5 block text-xs text-muted-foreground">
                Desabilitado, ninguém deste cliente entra — nem os contatos ativos.
              </span>
            </span>
          </label>

          <Campo id="ed-autocadastro" rotulo="Autocadastro">
            <select
              id="ed-autocadastro"
              value={autocadastro}
              onChange={(evento) => setAutocadastro(evento.target.value as PortalSignupMode)}
              className={CLASSE_DE_CAMPO}
            >
              {MODOS_DE_AUTOCADASTRO.map(([valor, rotulo]) => (
                <option key={valor} value={valor}>
                  {rotulo}
                </option>
              ))}
            </select>
          </Campo>

          <Campo
            id="ed-dominios"
            rotulo="Domínios aceitos no autocadastro"
            opcional
            dica="Separados por vírgula. Só domínio próprio do cliente."
          >
            <input
              id="ed-dominios"
              value={dominiosDoPortal}
              onChange={(evento) => setDominiosDoPortal(evento.target.value)}
              placeholder="contoso.com.br, contoso.com"
              className={CLASSE_DE_CAMPO}
            />
          </Campo>
        </fieldset>

        <Campo id="ed-notas" rotulo="Observações" opcional>
          <textarea
            id="ed-notas"
            value={observacoes}
            onChange={(evento) => setObservacoes(evento.target.value)}
            rows={3}
            maxLength={2_000}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {escrita.erro ? (
          <p role="alert" className="text-sm text-destructive">
            {escrita.erro}
          </p>
        ) : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button type="submit" loading={escrita.mutacao.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
